import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAudio } from '../contexts/AudioContext';
import { usePlaybackSession } from '../contexts/PlaybackSessionContext';
import { PLAYBACK_OWNERS } from '../lib/playbackSession';

const isVideoDetailRoute = (pathname) => /^\/(records|video-mixes)\/[^/]+/.test(pathname);

const resolvePlaybackOwner = (pathname, track = {}) => {
  if (isVideoDetailRoute(pathname)) return PLAYBACK_OWNERS.PLAYBLOCK;
  if (track.playback_surface === 'universal' || track.playbackSurface === 'universal') {
    return PLAYBACK_OWNERS.UNIVERSAL;
  }
  return PLAYBACK_OWNERS.MINI;
};

const GlobalPlaybackSessionBridge = () => {
  const location = useLocation();
  const { activeSource, currentTrack, isLoading, isPlaying, showPlayer } = useAudio();
  const { session, startSession, syncSession, transferOwner, updateRoute, endSession } = usePlaybackSession();
  const activeMediaKeyRef = useRef(null);

  const mediaKey = useMemo(() => {
    if (!showPlayer || activeSource !== 'youtube' || currentTrack?.type !== 'youtube') return null;
    return `${currentTrack.type}:${currentTrack.youtubeId || currentTrack.externalUrl || currentTrack.title}`;
  }, [activeSource, currentTrack, showPlayer]);

  useEffect(() => {
    if (!mediaKey) {
      if (activeMediaKeyRef.current) {
        activeMediaKeyRef.current = null;
        endSession();
      }
      return;
    }

    if (activeMediaKeyRef.current !== mediaKey) {
      activeMediaKeyRef.current = mediaKey;
      startSession({
        track: currentTrack,
        owner: resolvePlaybackOwner(location.pathname, currentTrack),
        route: location.pathname,
      });
    }
  }, [currentTrack, endSession, location.pathname, mediaKey, startSession]);

  useEffect(() => {
    if (!session.sessionId) return;
    syncSession({ state: isLoading ? 'buffering' : isPlaying ? 'playing' : 'paused' });
  }, [isLoading, isPlaying, session.sessionId, syncSession]);

  useEffect(() => {
    if (!session.sessionId) return;
    const nextOwner = resolvePlaybackOwner(location.pathname, currentTrack);
    transferOwner({
      owner: nextOwner,
      route: location.pathname,
      reason: nextOwner === PLAYBACK_OWNERS.MINI
        ? 'route-navigation'
        : nextOwner === PLAYBACK_OWNERS.UNIVERSAL
          ? 'universal-player-route'
          : 'return-to-video',
    });
    updateRoute(location.pathname);
  }, [currentTrack, location.pathname, session.sessionId, transferOwner, updateRoute]);

  return null;
};

export { isVideoDetailRoute, resolvePlaybackOwner };
export default GlobalPlaybackSessionBridge;
